import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classes from '../../../pipeline.module.scss';
import { Card, LineChart } from '../../PipelineComponents';
import GapDaysDistributionModal from './GapDaysDistributionModal';

const BUCKETS = [
  {
    key: 'bucket0',
    label: '0 days - no gap',
    tooltip: {
      text: 'Members who always refilled before running out.',
      steps: ['Normally the largest band by a wide margin.'],
      note: 'Members who have only filled once also sit here, because there is nothing yet to compare against.'
        + ' A big bar can mean new starts as much as good adherence.',
    },
  },
  {
    key: 'bucket1-7',
    label: '1–7 days',
    tooltip: {
      text: 'Members who went up to a week without medication.',
      steps: ['Counted on the cumulative total, not on a single late pickup.'],
      note: 'Usually a late pickup rather than someone stopping. The group most likely to be put right by a'
        + ' reminder call.',
    },
  },
  {
    key: 'bucket8-14',
    label: '8–14 days',
    tooltip: {
      text: 'Members who went one to two weeks without medication.',
      steps: ['Still short of the line that raises an adherence intervention.'],
      note: 'One more missed week and they cross the 14-day line that raises an adherence intervention in'
        + ' section 06.',
    },
  },
  {
    key: 'bucket15-29',
    label: '15–29 days',
    tooltip: {
      text: 'Members who went two weeks to a month without medication.',
      steps: ['Often larger than the 8–14 day band above it.'],
      note: 'Members are not drifting gradually — they drop straight past two weeks. Catching them in the'
        + ' 1–7 day band is where outreach pays.',
    },
  },
  {
    key: 'bucket30-59',
    label: '30–59 days',
    tooltip: {
      text: 'Members who missed a full month or more.',
      steps: ['These count toward the Gap ≥ 30 days figure above.'],
      note: 'Clinical follow-up rather than a routine reminder. A month off therapy is worth a pharmacist\'s'
        + ' attention.',
    },
  },
  {
    key: 'bucket60+',
    label: '60+ days',
    tooltip: {
      text: 'Members who missed two months or more.',
      steps: [
        'The top band — nothing sits above it.',
        'Also counted in Gap ≥ 30 days.',
      ],
      note: 'Most of these have probably moved pharmacy rather than fallen behind. Check retention in'
        + ' section 07 before spending calls.',
    },
  },
];

const GapDaysDistributionCard = ({
  params, buckets, loading, firstLoad,
}) => {
  const [modalData, setModalData] = useState({ open: false, bucket: {} });

  const distribution = useMemo(() => (buckets
    ? BUCKETS.map(({ key, label, tooltip }) => ({
      id: key, label, value: buckets[key] || 0, tooltip,
    }))
    : []), [buckets]);

  const onBucketClick = (item) => setModalData({
    open: true,
    bucket: { key: item.id, label: item.label },
  });

  return (
    <>
      <Card
        title="Gap-day distribution"
        caption="Cumulative gap days across the selected period"
        loading={loading}
        firstLoad={firstLoad}
        isEmpty={!distribution.length}
      >
        <LineChart
          data={distribution}
          className={classes.bordered}
          onClick={onBucketClick}
        />

        <p className={classes.note}>
          Members past
          {' '}
          <b>days supply + 7</b>
          {' '}
          auto-flag into the pipeline in section 03 - the gap report
          is what feeds it.
        </p>
      </Card>

      <GapDaysDistributionModal
        isOpen={modalData.open}
        params={params}
        bucket={modalData.bucket}
        onClose={() => setModalData((prev) => ({ ...prev, open: false }))}
      />
    </>
  );
};

GapDaysDistributionCard.propTypes = {
  params: PropTypes.object.isRequired,
  buckets: PropTypes.object,
  loading: PropTypes.bool,
  firstLoad: PropTypes.bool,
};

export default GapDaysDistributionCard;
